import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle, Package, ArrowRight, FileText, ShieldCheck, CreditCard } from 'lucide-react';
import { useStore } from '../context/StoreContext';
export function CheckoutConfirmation() {
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get('orderId');
  const {
    orders,
    currentUser
  } = useStore();
  const order = orders.find(o => o.id === orderId);
  if (!order) {
    return <div className="bg-gray-50 min-h-[80vh] flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8" data-id="element-1325">
        <div className="max-w-xl w-full bg-white rounded-2xl shadow-sm border border-gray-200 p-8 sm:p-12 text-center" data-id="element-1326">
          <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-1327">
            <Package className="w-10 h-10 text-gray-400" data-id="element-1328" />
          </div>
          <h1 className="text-3xl font-extrabold text-yarbo-dark mb-2" data-id="element-1329">
            Order Not Found
          </h1>
          <p className="text-gray-600 mb-8" data-id="element-1330">
            We couldn't find the order details you're looking for.
          </p>
          <Link to="/shop" className="inline-flex items-center justify-center gap-2 bg-yarbo-dark text-white font-bold px-6 py-3 rounded-lg hover:bg-black transition-colors" data-id="element-1331">
            Continue Shopping <ArrowRight className="w-4 h-4" data-id="element-1332" />
          </Link>
        </div>
      </div>;
  }
  const total = order.subtotalAfterDiscount !== undefined ? order.subtotalAfterDiscount + order.shippingCost + order.tax : order.items.reduce((sum, item) => sum + item.unitPrice * item.qty + (item.protection?.price || 0), 0) + order.shippingCost + order.tax;
  return <div className="bg-gray-50 min-h-screen py-12 px-4 sm:px-6 lg:px-8" data-id="element-1333">
      <div className="max-w-3xl mx-auto" data-id="element-1334">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden" data-id="element-1335">
          <div className="p-8 sm:p-12 text-center border-b border-gray-200" data-id="element-1336">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-1337">
              <CheckCircle className="w-10 h-10 text-green-600" data-id="element-1338" />
            </div>

            <h1 className="text-3xl font-extrabold text-yarbo-dark mb-2" data-id="element-1339">
              Thanks, you're all set!
            </h1>
            <p className="text-gray-600" data-id="element-1340">
              Your order{' '}
              <span className="font-bold text-gray-900" data-id="element-1341">{order.id}</span> is
              confirmed. We'll be in touch as your order moves.
            </p>
          </div>

          <div className="p-8 sm:p-12 bg-gray-50" data-id="element-1342">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8" data-id="element-1343">
              <div data-id="element-1344">
                <h3 className="text-sm font-bold text-gray-900 mb-2" data-id="element-1345">
                  Shipping Address
                </h3>
                <p className="text-sm text-gray-600" data-id="element-1346">
                  {order.customer.firstName} {order.customer.lastName}
                  <br data-id="element-1347" />
                  {order.shipping.line1}
                  <br data-id="element-1348" />
                  {order.shipping.line2 && <>
                      {order.shipping.line2}
                      <br data-id="element-1349" />
                    </>}
                  {order.shipping.city}, {order.shipping.state}{' '}
                  {order.shipping.zip}
                </p>
              </div>
              <div data-id="element-1350">
                <h3 className="text-sm font-bold text-gray-900 mb-2" data-id="element-1351">
                  Payment Method
                </h3>
                <p className="text-sm text-gray-600 flex items-center gap-2" data-id="element-1352">
                  <CreditCard className="w-4 h-4" data-id="element-1353" />
                  {order.payment.method} ending in {order.payment.last4}
                </p>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-1354">
              <div className="p-4 border-b border-gray-200 bg-gray-50" data-id="element-1355">
                <h3 className="font-bold text-gray-900" data-id="element-1356">Order Summary</h3>
              </div>
              <div className="divide-y divide-gray-100" data-id="element-1357">
                {order.items.map((item, idx) => <div key={idx} className="p-4 flex items-start gap-4" data-id="element-1358">
                    <div className="flex-1 min-w-0" data-id="element-1359">
                      <h4 className="text-sm font-bold text-gray-900" data-id="element-1360">
                        {item.productName}
                      </h4>
                      <p className="text-xs text-gray-500 mt-1" data-id="element-1361">
                        Qty: {item.qty}
                      </p>
                      {item.protection && <div className="text-xs text-yarbo-dark font-medium mt-1 flex items-center gap-1" data-id="element-1362">
                          <ShieldCheck className="w-3 h-3" data-id="element-1363" />
                          {item.protection.plan} (+${item.protection.price})
                        </div>}
                    </div>
                    <div className="text-sm font-bold text-gray-900" data-id="element-1364">
                      $
                      {((item.unitPrice + (item.protection?.price || 0)) * item.qty).toLocaleString()}
                    </div>
                  </div>)}
              </div>
              <div className="p-4 bg-gray-50 border-t border-gray-200 space-y-2 text-sm" data-id="element-1365">
                <div className="flex justify-between text-gray-600" data-id="element-1366">
                  <span data-id="element-1367">Subtotal</span>
                  <span data-id="element-1368">
                    $
                    {(order.subtotalBeforeDiscount !== undefined ? order.subtotalBeforeDiscount : order.items.reduce((sum, item) => sum + (item.unitPrice + (item.protection?.price || 0)) * item.qty, 0)).toLocaleString()}
                  </span>
                </div>
                {order.discountAmount && <div className="flex justify-between text-green-600 font-medium" data-id="element-1369">
                    <span data-id="element-1370">Discount ({order.discountCode})</span>
                    <span data-id="element-1371">-${order.discountAmount.toLocaleString()}</span>
                  </div>}
                <div className="flex justify-between text-gray-600" data-id="element-1372">
                  <span data-id="element-1373">Shipping</span>
                  <span data-id="element-1374">
                    {order.shippingCost === 0 ? 'Free' : `$${order.shippingCost.toLocaleString()}`}
                  </span>
                </div>
                <div className="flex justify-between text-gray-600" data-id="element-1375">
                  <span data-id="element-1376">Tax</span>
                  <span data-id="element-1377">${order.tax.toLocaleString()}</span>
                </div>
                <div className="flex justify-between font-bold text-lg text-gray-900 pt-2 border-t border-gray-200 mt-2" data-id="element-1378">
                  <span data-id="element-1379">Total</span>
                  <span data-id="element-1380">
                    $
                    {total.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="p-8 border-t border-gray-200 flex flex-col sm:flex-row gap-4 justify-center" data-id="element-1381">
            {currentUser && <Link to="/account" className="inline-flex items-center justify-center gap-2 bg-white border-2 border-gray-200 text-gray-700 font-bold px-6 py-3 rounded-lg hover:border-gray-300 hover:bg-gray-50 transition-colors" data-id="element-1382">
                <FileText className="w-4 h-4" data-id="element-1383" /> View Order in Account
              </Link>}
            <Link to="/shop" className="inline-flex items-center justify-center gap-2 bg-yarbo-dark text-white font-bold px-6 py-3 rounded-lg hover:bg-black transition-colors" data-id="element-1384">
              Continue Shopping <ArrowRight className="w-4 h-4" data-id="element-1385" />
            </Link>
          </div>
        </div>
      </div>
    </div>;
}